import * as THREE from 'three';
import {
  BED_RADIUS,
  bedLayout,
  CLIMB_HALF,
  CLIMB_LEDGES,
  CLIMB_RADIUS,
  climbLayout,
  CRACK_HALF,
  createClearingMap,
  DEN_AT,
  DEN_R,
  HALL_R,
  hexLen,
  hexToWorld,
  ROCK_HALL_RADIUS,
  rockHallLayout,
  TOP_AT,
  TOP_R,
  WOLF_RADIUS,
  wolfLayout,
  type BattleState,
  type BedLayout,
  type CazaPt,
  type ClimbLayout,
  type HallLayout,
  type MapDef,
  type WolfLayout,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, looksRng, Precip, type OmenCtx } from '../omens/common.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import { seaMat } from './bote.ts';
import { longBoat, rower, skiffBoat, woodenBoar } from './roca-props.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Long Hunt's chapter 5 places (docs/sendas/barbaro.md, «La guarida»): a black rock alone in the sea at the edge of
 * the titan's shadow, the foam climbing its walls, a cave's mouth up top; inside, the hall of the four mouths down to
 * the sea, the way down to the ash bed, the den and the summit over the clan's boats. Every floor is a ground of its
 * own (one heightfield mesh, so the people and the pack ride its ledges and its summit: `heightAt`), never the board's
 * hex tiles.
 *
 * - `rocaSea` (the cinematic): the rock from the sea at dawn, Sten's small boat at its foot, the clan's boats behind.
 * - `rocaWall` (the opening, act 1): the rock's south wall, five ledges zig-zagging up it to the cave's mouth, wet and
 *   bright with the dawn, lichen and the crows' nests on their lips, the sea breaking below.
 * - `rocaMouth` (the campfire): the wall's top, the cave's mouth at dusk, the fire.
 * - `rocaHall` (act 2): the hall of the four mouths, each a pit of dark water under an arch of rock whose cracks glow
 *   red (one, two; three and it comes down in a roar of rubble and spray); the cracks in the roof that join the arches
 *   seen as blades of daylight on the floor (red a moment when one comes down); at each mouth's entrance the tracks in
 *   the ash: the Wolf's great prints across three stepping stones and never at the puddle, the pack's small ones round
 *   it; Ivar hurt against the west wall.
 * - `rocaBed` (act 3, the bottom): the low galleries down, ash on the floor like dirty snow, boar and seal bones, the
 *   way on heaped with ash and bones until it opens, the bed of ash at the back under a shaft of light with the boar.
 * - `rocaDen`, `rocaTop` (the speech, act 4, the close): the den round the ash bed with its four mouths (open: dark
 *   water under an arch; shut: rubble), four cracked pillars to the roof, the slabs on the way to the mouths, the crack
 *   up to the summit; the summit's three edges over the sea far below, its loose stones, the clan's boats under the
 *   edge they close, Sten's small boat at the south-west wall, the White Island far to the east.
 * - `rocaIsla` (the way back): the White Island's shore from the sea, Bera small by her fire and the stone of strokes,
 *   the clan's boats going by.
 *
 * Draw calls: the ground (one), the dressing (one merged mesh), the sea, the sky, the glow layer, the spray or the ash
 * in the air, and what changes: four arches with their rubble, four roof cracks, the tracks (instanced), two gates,
 * four pillars, four slabs, four stones, the clan's boats (instanced, one), the marks of the Wolf's look and leap (two),
 * Terrón (five). No lights of its own: the board's are repainted (HOURS).
 */

export const ROCA_PLACES = new Set(['rocaSea', 'rocaWall', 'rocaMouth', 'rocaHall', 'rocaBed', 'rocaDen', 'rocaTop', 'rocaIsla']);

/** The scenes' boards' seed (client game/cine.ts builds the same). */
export const ROCA_SCENE_SEED = 7415;

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const FIRE = new THREE.Color('#ff9a5a');
const DAY = new THREE.Color('#dfe9f6');
const ASH = new THREE.Color('#c9ced4');

/** How high each ledge of the wall stands (world units), and the wall's lip over the next. */
const STEP = 1.45;
const LIP = 0.32;
/** The summit over the den, the rock's mass round them, the sea far below. */
const TOP_H = 5.0;
const MASS_H = 4.3;
const DEEP_SEA = -7.2;

type Hour = 'dawn' | 'day' | 'dusk' | 'night' | 'inside' | 'den';

const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sea: [string, string, string]; dark: string }> = {
  // A cold dawn in the fog off the sea: the wet rock catching it, never black.
  dawn: { bg: '#7d8a99', fog: ['#8794a3', 48, 150], hemi: ['#cfd9e6', '#2c3037', 1.7], key: ['#f6ece4', 1.45], rim: ['#a9bdd6', 1.1], sky: ['#55647a', '#b6c1cd', '#f3f1ee'], sea: ['#1f3a4c', '#476b80', '#e9f1f6'], dark: '#06111a' },
  // A bright day after the Wolf: the sun out between the clouds.
  day: { bg: '#7f93a6', fog: ['#8fa2b4', 60, 180], hemi: ['#dbe4ee', '#363b42', 1.8], key: ['#fbf3ea', 1.7], rim: ['#b4c6dc', 1.0], sky: ['#4b6683', '#a9bccd', '#f5f7f9'], sea: ['#1f4560', '#4f7a92', '#eef4f8'], dark: '#061420' },
  dusk: { bg: '#3a2e38', fog: ['#43404b', 40, 130], hemi: ['#c9d3e4', '#2c3038', 2.1], key: ['#ffad86', 1.25], rim: ['#93a5e2', 1.15], sky: ['#25222f', '#b05a49', '#f0b49a'], sea: ['#1b2938', '#40546a', '#dfe2ea'], dark: '#060a10' },
  night: { bg: '#18202a', fog: ['#1c2530', 34, 110], hemi: ['#9bb2cd', '#151a22', 1.9], key: ['#c4d6ec', 1.0], rim: ['#8aa8d6', 1.0], sky: ['#0c1320', '#1b2c3c', '#33465a'], sea: ['#0f1c27', '#26404f', '#b8cad6'], dark: '#02060a' },
  // Inside the rock: cold daylight down the roof's cracks, the ash pale under it.
  // The den and the summit (the Wolf's fight): the dawn's light down the roof's crack and off the sea, hard and cold on
  // the black rock, the sky's fill low (the first den in the dawn's flat fill read as one grey: the Wolf grey on grey ash).
  den: { bg: '#465160', fog: ['#4b5766', 44, 150], hemi: ['#c3cfdd', '#15181d', 1.05], key: ['#fff2e8', 2.15], rim: ['#9fb8da', 1.5], sky: ['#3e4c60', '#97a6b6', '#e9ecef'], sea: ['#0f2433', '#33546a', '#dfe9f0'], dark: '#030a10' },
  inside: { bg: '#1f252c', fog: ['#232930', 34, 95], hemi: ['#b9c6d8', '#22262c', 1.65], key: ['#d6e1ef', 1.05], rim: ['#8ea6c8', 1.0], sky: ['#1a1f26', '#262c34', '#343b44'], sea: ['#0b1822', '#1f3646', '#9fb4c4'], dark: '#02060a' },
};

const hourOf = (place: string): Hour => (place === 'rocaHall' || place === 'rocaBed' ? 'inside' : place === 'rocaMouth' ? 'dusk' : place === 'rocaDen' ? 'den' : place === 'rocaTop' || place === 'rocaIsla' ? 'day' : 'dawn');

const len = (dx: number, dy: number): number => Math.hypot(dx, dy);
const seg = (px: number, py: number, a: CazaPt, b: CazaPt): { d: number; t: number } => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L2 = dx * dx + dy * dy;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / L2)) : 0;
  return { d: len(px - (a.x + dx * t), py - (a.y + dy * t)), t };
};
const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
/** A cheap integer-free value noise for the rock (sim units in). */
const noise = (x: number, y: number): number => {
  const h = (n: number): number => {
    const s = Math.sin(n) * 43758.5453;
    return s - Math.floor(s);
  };
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const a = h(xi * 127.1 + yi * 311.7);
  const b = h((xi + 1) * 127.1 + yi * 311.7);
  const c = h(xi * 127.1 + (yi + 1) * 311.7);
  const d = h((xi + 1) * 127.1 + (yi + 1) * 311.7);
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
};

const cellsOf = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

/** A scene's board (client game/cine.ts): the act's own for the places a fight is on, a clearing for the others. */
export const rocaSceneMap = (place: string): MapDef | null => {
  if (place === 'rocaWall' || place === 'rocaMouth') return { ...createClearingMap(CLIMB_RADIUS), rocks: climbLayout(ROCA_SCENE_SEED).rocks };
  if (place === 'rocaHall') return { ...createClearingMap(ROCK_HALL_RADIUS), rocks: rockHallLayout(ROCA_SCENE_SEED).rocks };
  if (place === 'rocaBed') return { ...createClearingMap(BED_RADIUS), rocks: bedLayout(ROCA_SCENE_SEED).rocks };
  if (place === 'rocaDen' || place === 'rocaTop') return { ...createClearingMap(WOLF_RADIUS), rocks: wolfLayout(ROCA_SCENE_SEED).rocks };
  if (place === 'rocaSea' || place === 'rocaIsla') {
    const m = createClearingMap(12);
    return { ...m, rocks: cellsOf(m.radius).filter((h) => hexToWorld(h.q, h.r).y < -2500) };
  }
  return null;
};

interface Arch {
  stand: THREE.Mesh;
  rubble: THREE.Mesh;
  cracks: THREE.Mesh[];
  at: THREE.Vector3;
  down: boolean;
}

interface Piece {
  up: THREE.Mesh;
  down: THREE.Mesh | null;
  at: THREE.Vector3;
}

export class RocaLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow: GlowLayer;
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private air: Precip | null = null;
  private sea: THREE.ShaderMaterial | null = null;
  private seaY = -1.1;
  private now = 0;
  private hour: Hour;
  private rnd = looksRng(0x50ca);
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private look = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private last: BattleState | null = null;
  private seed: number;
  private mat: THREE.MeshStandardMaterial;
  private cold: THREE.MeshStandardMaterial;
  private height: (x: number, y: number) => number = () => 0;
  /** Where the place is open sea (its ground is not drawn there: the figures stand in the boats). */
  private water: ((x: number, y: number) => boolean) | null = null;
  // The wall.
  private climb: ClimbLayout | null = null;
  // The hall.
  private hall: HallLayout | null = null;
  private arches: Arch[] = [];
  private blades: { mesh: THREE.Mesh; a: number; b: number; red: number }[] = [];
  private cracksSeen = '';
  // The bed.
  private bed: BedLayout | null = null;
  private gates: THREE.Mesh[] = [];
  private gatesOpen = -1;
  // The den and the summit.
  private den: WolfLayout | null = null;
  private pillars: Piece[] = [];
  private stones: Piece[] = [];
  private slabs: { tile: THREE.Mesh; cracks: THREE.Mesh[]; hole: THREE.Mesh }[] = [];
  private slabKey = '';
  private boats: THREE.InstancedMesh | null = null;
  private boatSpots: { x: number; z: number; yaw: number }[] = [];
  private boatAt = new THREE.Vector3();
  private boatTo = new THREE.Vector3();
  private boatYaw = 0;
  private skiffMesh: THREE.Mesh | null = null;
  private lookRing: THREE.Mesh | null = null;
  private leapMark: THREE.Mesh | null = null;
  private howlRing: THREE.Mesh | null = null;
  private dummy = new THREE.Object3D();
  // Terrón.
  private tBody: THREE.Mesh | null = null;
  private tLegs: THREE.Mesh[] = [];
  private tTrack = { px: 0, py: 0, cx: 0, cy: 0, dist: 0, set: false };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `roca-${place}`;
    this.hour = hourOf(place);
    this.seed = s.rules?.senda ? s.seed : ROCA_SCENE_SEED;
    this.glow = new GlowLayer(ctx.low ? 180 : 360);
    this.mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.78, metalness: 0.05, flatShading: true }));
    // (The pale things lit cold from within: under a warm light they never turn cream.)
    this.cold = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.05, flatShading: true, emissive: '#5d6878' }));
    const kit = new Kit();
    const pale = new Kit();
    if (place === 'rocaWall' || place === 'rocaMouth') this.buildWall(kit, pale);
    else if (place === 'rocaHall') this.buildHall(kit, pale, s);
    else if (place === 'rocaBed') this.buildBed(kit, pale);
    else if (place === 'rocaDen' || place === 'rocaTop') this.buildDen(kit, pale, s);
    else if (place === 'rocaIsla') this.buildIsla(kit, pale);
    else this.buildSea(kit, pale);
    this.buildWater();
    this.buildGround();
    this.buildTerron();
    for (const [k, m, name] of [
      [kit, this.mat, 'roca-dress'],
      [pale, this.cold, 'roca-pale'],
    ] as const) {
      const dress = k.build(m, name);
      if (!dress) continue;
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    if (!ctx.crypt) {
      const inside = this.hour === 'inside';
      // Spray off the sea outside; ash hanging in the light inside.
      this.air = inside
        ? new Precip({ count: ctx.low ? 90 : 180, box: [16, 5, 16], fall: 0.12, wind: [0.15, 0.05], sway: 0.4, size: [0.025, 0.05], color: '#d3d9e0', opacity: 0.32, round: true }, 51)
        : new Precip({ count: ctx.low ? 120 : 240, box: [16, 6, 16], fall: 0.6, wind: [2.6, -0.6], sway: 0.8, size: [0.025, 0.06], color: '#eef3f7', opacity: 0.28, round: true }, 52);
      this.group.add(this.air.mesh);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color, size, a, seed: this.lights.length * 1.37, flick });
  }

  /** The ground's height at a sim point (world units): what the board's figures stand on. */
  heightAt(x: number, y: number): number {
    return this.height(x, y);
  }

  private at(p: CazaPt, h = 0): THREE.Vector3 {
    return new THREE.Vector3(p.x * K, this.height(p.x, p.y) + h, p.y * K);
  }

  private mesh(geo: THREE.BufferGeometry, mat: THREE.Material, name: string): THREE.Mesh {
    const m = new THREE.Mesh(this.track(geo), mat);
    m.name = name;
    m.castShadow = true;
    m.receiveShadow = true;
    this.group.add(m);
    return m;
  }

  /** One kit built to its own mesh (a piece that shows and hides). */
  private piece(build: (k: Kit) => void, name: string, mat = this.mat): THREE.Mesh {
    const k = new Kit();
    build(k);
    const m = k.build(mat, name);
    if (!m) return this.mesh(new THREE.BufferGeometry(), mat, name);
    m.castShadow = true;
    m.receiveShadow = true;
    this.geos.push(m.geometry);
    this.group.add(m);
    return m;
  }

  /** The sim's radius of the board this place is on, for the ground's extent. */
  private extent(): number {
    return (this.map.radius + 3) * 1000;
  }

  /* ---------- the ground ---------- */

  /** The ground: one heightfield over the board, coloured by what it is (ledge, wall, ash, snow, rock, sea's edge). */
  private buildGround(): void {
    if (this.place === 'rocaSea') return;
    const E = this.extent();
    const step = this.ctx.low ? 320 : 240;
    const n = Math.ceil((E * 2) / step);
    const geo = new THREE.PlaneGeometry(E * 2 * K, E * 2 * K, n, n);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const col = new Float32Array(pos.count * 3);
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) / K;
      const y = pos.getZ(i) / K;
      const h = this.water?.(x, y) ? this.seaY - 2 : this.height(x, y);
      pos.setY(i, h);
      // The slope: steep is the wall's face, wet and dark with streaks; flat is the floor.
      const dh = Math.max(Math.abs(this.height(x + step, y) - h), Math.abs(this.height(x, y + step) - h), Math.abs(this.height(x - step, y) - h), Math.abs(this.height(x, y - step) - h));
      this.tint(c, x, y, h, dh);
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.computeVertexNormals();
    const m = this.mesh(geo, this.mat, 'roca-ground');
    m.castShadow = false;
  }

  /**
   * The ground's colour at a point by the place. (Far darker than it shows: a floor's face takes the whole sky and the
   * sun; `#5b6066` read almost white. docs/RENDIMIENTO.md.)
   */
  private tint(c: THREE.Color, x: number, y: number, h: number, dh: number): void {
    const nz = noise(x / 900, y / 900);
    const fine = noise(x / 260 + 7, y / 260 - 3);
    const steep = smooth(0.18, 0.7, dh);
    const p = this.place;
    if ((p === 'rocaDen' || p === 'rocaTop') && h < 0.4 && this.inDen(x, y) && this.den) {
      // The den's floor: black wet rock at its edge, the ash pale and thick round the bed and thinning out in drifts
      // toward the mouths (so the grey Wolf reads dark on the ash and pale on the rock).
      const bd = len(x - this.den.bed.x, y - this.den.bed.y);
      const ash = Math.min(1, (1 - smooth(1400, DEN_R - 600, bd)) * 0.85 + Math.max(0, nz - 0.55) * 1.1) * (0.55 + fine * 0.45);
      c.set('#1d2126').lerp(new THREE.Color('#2a3036'), fine * 0.5).lerp(new THREE.Color('#5a6168'), ash);
      c.lerp(new THREE.Color('#15181c'), steep * 0.9);
      return;
    }
    if (p === 'rocaHall' || p === 'rocaBed' || ((p === 'rocaDen' || p === 'rocaTop') && h < 0.4 && this.inDen(x, y))) {
      // Ash on the cave's floor like dirty snow, the rock showing through; the walls black and wet.
      if (h > 0.6) c.set('#262b31').lerp(new THREE.Color('#3b434c'), fine * 0.6).lerp(new THREE.Color('#56616c'), Math.max(0, nz - 0.72) * 1.6);
      else c.set('#34383d').lerp(new THREE.Color('#42474d'), nz).lerp(new THREE.Color('#25292d'), Math.max(0, fine - 0.55) * 1.8);
      c.lerp(new THREE.Color('#22262b'), steep * 0.9);
      return;
    }
    if (p === 'rocaIsla' && h > -0.2 && y < 0) {
      c.set('#e3e9ef').lerp(new THREE.Color('#aeb8c2'), Math.max(0, fine - 0.4)).lerp(new THREE.Color('#5c646d'), steep);
      return;
    }
    if (h < this.seaY + 0.15) {
      c.set('#2f3940').lerp(new THREE.Color('#556169'), fine * 0.4);
      return;
    }
    // The black rock: dark slate with lichen, wet and bright on its faces; the ledges' floors paler, worn.
    const floor = steep < 0.3;
    if (floor) c.set('#353b42').lerp(new THREE.Color('#444b53'), nz * 0.8).lerp(new THREE.Color('#3a4a33'), Math.max(0, fine - 0.6) * 1.9);
    else c.set('#23272d').lerp(new THREE.Color('#353c44'), fine * 0.7).lerp(new THREE.Color('#5a6873'), Math.max(0, nz - 0.74) * 1.5);
    // The summit under the wind: paler, salt-crusted; the rock's mass round the den a dark top, barely varied.
    if (p === 'rocaDen' || p === 'rocaTop') {
      if (floor && h > TOP_H - 0.3) c.lerp(new THREE.Color('#545c64'), 0.45);
      // (The mass round the den one dark top, steep or not: its noise's slopes lit pale read as terraces of blocks.)
      else if (h > 1 && h < TOP_H - 0.3) c.set('#1c2025').lerp(new THREE.Color('#262b31'), fine);
    }
  }

  private inDen(x: number, y: number): boolean {
    return len(x - DEN_AT.x, y - DEN_AT.y) < DEN_R + 200;
  }

  /* ---------- the sea ---------- */

  private buildWater(): void {
    const inside = this.place === 'rocaHall' || this.place === 'rocaBed';
    const sea = this.track(seaMat());
    this.sea = sea;
    this.paintSea();
    const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(inside ? 60 : 320, inside ? 60 : 320, 1, 1)), sea);
    m.rotation.x = -Math.PI / 2;
    m.position.y = this.seaY;
    m.renderOrder = -2;
    this.group.add(m);
  }

  private paintSea(): void {
    const sea = this.sea;
    if (!sea) return;
    const look = HOURS[this.hour];
    const u = sea.uniforms;
    (u.deep as THREE.IUniform<THREE.Color>).value.set(look.sea[0]);
    (u.shallow as THREE.IUniform<THREE.Color>).value.set(look.sea[1]);
    (u.foamC as THREE.IUniform<THREE.Color>).value.set(look.sea[2]);
    (u.darkC as THREE.IUniform<THREE.Color>).value.set(look.dark);
    (u.rough as THREE.IUniform<number>).value = this.place === 'rocaIsla' ? 0.08 : 0.2;
  }

  /** Foam where the sea breaks on rock: flattened white stones in a ragged band along a line. */
  private foam(kit: Kit, pts: THREE.Vector3[], n: number, spread: number): void {
    const g = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i < n; i++) {
      const a = pts[Math.floor(this.rnd() * pts.length)] as THREE.Vector3;
      kit.add(g, this.rnd() < 0.6 ? '#cfdbe3' : '#a9bcc9', place(a.x + (this.rnd() - 0.5) * spread, this.seaY + 0.02, a.z + (this.rnd() - 0.5) * spread, this.rnd() * 6, new THREE.Vector3(0.05 + this.rnd() * 0.1, 0.015, 0.04 + this.rnd() * 0.08)));
    }
    g.dispose();
  }

  /**
   * A mouth down to the sea: a hole of dark water in the floor, its wet lip pale, a cold light off the water. (Not a pit
   * in the ground's heightfield: at the grid's step a pit's walls came out as white needles; docs/RENDIMIENTO.md.)
   */
  private holeAt(x: number, z: number): void {
    const water = this.track(new THREE.MeshStandardMaterial({ color: '#0d1820', roughness: 0.15, metalness: 0.3, emissive: '#0b1a24' }));
    const m = this.mesh(new THREE.CircleGeometry(0.95, 24), water, 'roca-hole');
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, 0.025, z);
    m.castShadow = false;
    const lip = this.mesh(new THREE.RingGeometry(0.92, 1.12, 24), this.track(new THREE.MeshStandardMaterial({ color: '#3a434c', roughness: 0.3, metalness: 0.1 })), 'roca-hole-lip');
    lip.rotation.x = -Math.PI / 2;
    lip.position.set(x, 0.028, z);
    lip.castShadow = false;
    this.light(new THREE.Vector3(x, 0.3, z), new THREE.Color('#5d88a6'), 1.8, 0.18, 0.35);
  }

  /** Boulders and chips of black rock about a point (world). */
  private rubbleAt(kit: Kit, x: number, y: number, z: number, n: number, r: number, size = 0.3): void {
    const g = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i < n; i++) {
      const a = this.rnd() * Math.PI * 2;
      const d = Math.sqrt(this.rnd()) * r;
      const s = size * (0.5 + this.rnd());
      kit.add(g, ['#383e46', '#2c3138', '#4a525b', '#565e67'][i % 4] as string, place(x + Math.cos(a) * d, y + s * 0.4, z + Math.sin(a) * d, this.rnd() * 6, new THREE.Vector3(s, s * (0.6 + this.rnd() * 0.5), s * (0.8 + this.rnd() * 0.4)), this.rnd() * 0.5, this.rnd() * 0.5));
    }
    g.dispose();
  }

  /* ---------- the wall (act 1, the opening, the campfire) ---------- */

  private buildWall(kit: Kit, pale: Kit): void {
    const L = climbLayout(this.seed);
    this.climb = L;
    this.seaY = -1.25;
    const ys = CLIMB_LEDGES;
    // Each corner's height: the ledges at their step, each ramp's top at the next; the last climb to the mouth.
    const hc = L.corners.map((p, i) => {
      const k = ys.findIndex((y) => Math.abs(y - p.y) < 30);
      if (i === L.corners.length - 1) return STEP * 4 + 0.9;
      return (k >= 0 ? k : 4) * STEP;
    });
    const mouth = L.mouth;
    this.height = (x, y) => {
      let best = 1e9;
      let h = 0;
      for (let c = 0; c + 1 < L.corners.length; c++) {
        const r = seg(x, y, L.corners[c] as CazaPt, L.corners[c + 1] as CazaPt);
        if (r.d < best) {
          best = r.d;
          h = (hc[c] as number) + ((hc[c + 1] as number) - (hc[c] as number)) * r.t;
        }
      }
      const md = len(x - mouth.x, y - mouth.y);
      if (md < 1500) {
        best = 0;
        h = STEP * 4 + 0.9;
      }
      if (best <= CLIMB_HALF - 100) return h + (noise(x / 500, y / 500) - 0.5) * 0.06;
      const y0 = ys[0] as number;
      // South of the lowest ledge: its foot in the sea.
      if (y > y0 + CLIMB_HALF) return this.seaY - 0.4 + Math.max(0, 1 - (y - y0 - CLIMB_HALF) / 1500) * (h + 0.6) * 0.5;
      // The rock between two ledges stands at the upper one, a lip over it; above the last, the rock's high wall.
      let k = 0;
      while (k + 1 < ys.length && y < (ys[k + 1] as number) + CLIMB_HALF) k++;
      const wallH = y < (ys[ys.length - 1] as number) - CLIMB_HALF - 300 ? STEP * 4 + 4.5 + noise(x / 1200, y / 1200) * 1.2 : (k + 1) * STEP + LIP + noise(x / 700, y / 700) * 0.25;
      // (Blended into the ledge at its edge, so the face reads as a step, not a seam.)
      const edge = smooth(CLIMB_HALF - 100, CLIMB_HALF + 250, best);
      return h + (Math.max(wallH, h) - h) * edge;
    };
    // The foam at the wall's foot, the swell breaking on it: a soft white band and its flecks, half-sunk boulders.
    const footZ = ((ys[0] as number) + CLIMB_HALF + 650) * K;
    const foot: THREE.Vector3[] = [];
    for (let x = -9000; x <= 9000; x += 600) foot.push(new THREE.Vector3(x * K, 0, footZ));
    this.foam(kit, foot, this.ctx.low ? 50 : 100, 1.6);
    for (let i = 0; i < 16; i++) this.rubbleAt(kit, (this.rnd() - 0.5) * 22, this.seaY - 0.15, footZ + (this.rnd() - 0.3) * 1.6, 1, 0.2, 0.45);
    const band = this.mesh(new THREE.PlaneGeometry(26, 1.3).rotateX(-Math.PI / 2), this.track(new THREE.MeshBasicMaterial({ color: '#dfe9f0', transparent: true, opacity: 0.22, depthWrite: false })), 'roca-foam');
    band.position.set(0, this.seaY + 0.03, footZ - 0.1);
    band.castShadow = false;
    band.receiveShadow = false;
    // Boulders along the ledges' sea lips, lichen tufts, the crows' nests on the lips above.
    const tuft = new THREE.IcosahedronGeometry(1, 0);
    for (let i = 0; i < L.way.length; i += 2) {
      const p = L.way[i] as CazaPt;
      const s = (L.ledgeOf[i] as number) % 2 === 0 ? 1 : -1;
      void s;
      if (this.rnd() < 0.45) this.rubbleAt(kit, p.x * K + (this.rnd() - 0.5) * 2, this.height(p.x, p.y + CLIMB_HALF - 350), (p.y + CLIMB_HALF - 300) * K, 2, 0.4, 0.22);
      if (this.rnd() < 0.3) kit.add(tuft, '#566b4f', place(p.x * K + (this.rnd() - 0.5) * 2.4, this.height(p.x, p.y - CLIMB_HALF) + 0.05, (p.y - CLIMB_HALF + 120) * K, 0, new THREE.Vector3(0.3, 0.08, 0.2)));
    }
    tuft.dispose();
    const twig = new THREE.CylinderGeometry(0.02, 0.02, 1, 4);
    const egg = new THREE.SphereGeometry(1, 6, 4);
    for (const n of L.nests) {
      const base = this.height(n.x, n.y);
      const x = n.x * K;
      const z = n.y * K;
      for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2;
        kit.add(twig, i % 2 ? '#4a3d32' : '#5d4c3d', place(x + Math.cos(a) * 0.35, base + 0.1 + (i % 3) * 0.04, z + Math.sin(a) * 0.35, a + Math.PI / 2, new THREE.Vector3(1, 0.7, 1), Math.PI / 2, 0.3));
      }
      for (let i = 0; i < 2; i++) pale.add(egg, '#d7dde3', place(x + (i - 0.5) * 0.14, base + 0.16, z, 0, new THREE.Vector3(0.07, 0.09, 0.07)));
    }
    twig.dispose();
    egg.dispose();
    // The cave's mouth: a black arch in the high wall, teeth of rock round it, the dark inside.
    const mx = mouth.x * K;
    const mz = (mouth.y - 600) * K;
    const mh = STEP * 4 + 0.9;
    const blk = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i <= 12; i++) {
      const a = Math.PI * (i / 12);
      kit.add(blk, i % 2 ? '#2a2f36' : '#3a4048', place(mx + Math.cos(a) * 2.2, mh + Math.sin(a) * 2.4, mz - 0.6, a, new THREE.Vector3(0.7, 0.6, 0.9)));
    }
    kit.add(new THREE.CircleGeometry(1, 20, 0, Math.PI), '#05080c', place(mx, mh + 0.02, mz - 0.9, 0, new THREE.Vector3(2.0, 2.3, 1)));
    kit.add(new THREE.PlaneGeometry(4.2, 0.1).rotateX(-Math.PI / 2), '#0a0e13', place(mx, mh + 0.03, mz - 0.4, 0, 1));
    blk.dispose();
    this.light(new THREE.Vector3(mx, mh + 0.9, mz - 1.1), RED, 1.4, 0.12, 0.4);
    if (this.place === 'rocaMouth') {
      // The campfire's own glow, the sea far below catching the dusk.
      this.light(new THREE.Vector3(mx, mh + 0.6, mz + 1.4), FIRE, 4.5, 0.22, 1);
    }
  }

  /* ---------- the hall (act 2) ---------- */

  private archMesh(m: CazaPt, toward: CazaPt): { stand: THREE.Mesh; rubble: THREE.Mesh; cracks: THREE.Mesh[]; at: THREE.Vector3 } {
    const at = new THREE.Vector3(m.x * K, 0, m.y * K);
    const yaw = Math.atan2(toward.x - m.x, toward.y - m.y);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    // (Local x across the mouth, z toward the hall's middle.)
    const P = (lx: number, ly: number, lz: number, sc: THREE.Vector3, rz = 0): THREE.Matrix4 => place(at.x + lx * c + lz * s, ly, at.z - lx * s + lz * c, yaw, sc, 0, rz);
    const blk = new THREE.BoxGeometry(1, 1, 1);
    const stone = new THREE.DodecahedronGeometry(1, 0);
    const stand = this.piece((k) => {
      for (const side of [-1, 1]) {
        k.add(stone, '#59616a', P(side * 1.15, 0.9, 0.2, new THREE.Vector3(0.5, 1.1, 0.55)));
        k.add(stone, '#4b535c', P(side * 1.05, 1.9, 0.25, new THREE.Vector3(0.45, 0.7, 0.5)));
      }
      // The arch's span over the dark water, in blocks.
      for (let i = 0; i <= 8; i++) {
        const a = Math.PI * (i / 8);
        k.add(blk, i % 2 ? '#646c75' : '#555d66', P(Math.cos(a) * 1.15, 1.95 + Math.sin(a) * 0.75, 0.25, new THREE.Vector3(0.52, 0.42, 0.62)), new THREE.Matrix4().makeRotationZ(a - Math.PI / 2));
      }
      // Its lip of wet stone round the hole.
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        k.add(stone, '#4a525b', P(Math.cos(a) * 0.95, 0.03, Math.sin(a) * 0.85, new THREE.Vector3(0.3, 0.1, 0.25)));
      }
    }, 'roca-arch');
    const rubble = this.piece((k) => {
      for (let i = 0; i < 22; i++) {
        const a = this.rnd() * Math.PI * 2;
        const d = Math.sqrt(this.rnd()) * 1.1;
        const sz = 0.25 + this.rnd() * 0.35;
        k.add(stone, ['#3a4048', '#2f343b', '#4b535c'][i % 3] as string, place(at.x + Math.cos(a) * d, -0.2 + this.rnd() * 0.5 + (1.1 - d) * 0.5, at.z + Math.sin(a) * d, this.rnd() * 6, new THREE.Vector3(sz, sz * 0.8, sz)));
      }
    }, 'roca-rubble');
    rubble.visible = false;
    const cracks: THREE.Mesh[] = [];
    const glowMat = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a' }));
    for (let i = 0; i < 3; i++) {
      const lx = [-0.55, 0.05, 0.6][i] as number;
      const ly = [2.35, 2.62, 2.3][i] as number;
      const m = this.mesh(new THREE.BoxGeometry(0.07, 0.55, 0.04), glowMat, 'roca-crack');
      m.position.set(at.x + lx * c + 0.58 * s, ly, at.z - lx * s + 0.58 * c);
      m.rotation.set(0, yaw, (i - 1) * 0.5);
      m.castShadow = false;
      m.visible = false;
      cracks.push(m);
    }
    blk.dispose();
    stone.dispose();
    this.holeAt(at.x, at.z);
    return { stand, rubble, cracks, at };
  }

  /** The Wolf's prints in the den's ash: a line of great pads from its bed toward a mouth, pale on the dark rock. */
  private denTrail(pale: Kit, from: CazaPt, to: CazaPt): void {
    const pad = new THREE.CircleGeometry(1, 8).rotateX(-Math.PI / 2);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const d = Math.max(1, len(dx, dy));
    const ux = dx / d;
    const uy = dy / d;
    const yaw = Math.atan2(ux, uy);
    const n = Math.floor((d - 2600) / 700);
    for (let i = 0; i < n; i++) {
      const t = 1500 + i * 700;
      const side = i % 2 ? 1 : -1;
      const x = (from.x + ux * t - uy * side * 220) * K;
      const z = (from.y + uy * t + ux * side * 220) * K;
      pale.add(pad, '#b7c0c8', place(x, 0.04, z, yaw, new THREE.Vector3(0.2, 1, 0.17)));
      for (let k = 0; k < 4; k++) {
        const a = yaw + (k - 1.5) * 0.4;
        pale.add(pad, '#b7c0c8', place(x + Math.sin(a) * 0.24, 0.041, z + Math.cos(a) * 0.24, 0, new THREE.Vector3(0.065, 1, 0.06)));
      }
    }
    pad.dispose();
  }

  /** The tracks at a mouth's entrance: the pack's small prints round a puddle; the Wolf's great ones across the stones. */
  private tracks(kit: Kit, pale: Kit, at: CazaPt, center: CazaPt, wolf: boolean): void {
    const dx = center.x - at.x;
    const dy = center.y - at.y;
    const d = Math.max(1, len(dx, dy));
    const ux = dx / d;
    const uy = dy / d;
    const px = at.x + ux * 1700;
    const py = at.y + uy * 1700;
    // (Laid flat in the geometry before scaling: a disc scaled in Y and then tipped stood up in white blades.)
    const pud = new THREE.CircleGeometry(1, 14).rotateX(-Math.PI / 2);
    const pad = new THREE.CircleGeometry(1, 8).rotateX(-Math.PI / 2);
    // The puddle: dark water, the sky's pale in it.
    kit.add(pud, '#16222a', place(px * K, 0.025, py * K, 0, new THREE.Vector3(0.6, 1, 0.45)));
    kit.add(pud, '#4a6070', place(px * K + 0.1, 0.03, py * K - 0.06, 0, new THREE.Vector3(0.32, 1, 0.16)));
    const print = (x: number, z: number, yaw: number, s: number): void => {
      // A pad and four toes, pale in the ash.
      pale.add(pad, '#c9d0d6', place(x, 0.035, z, yaw, new THREE.Vector3(s * 0.5, 1, s * 0.42)));
      for (let t = 0; t < 4; t++) {
        const a = yaw + (t - 1.5) * 0.42;
        pale.add(pad, '#c9d0d6', place(x + Math.sin(a) * s * 0.62, 0.036, z + Math.cos(a) * s * 0.62, 0, new THREE.Vector3(s * 0.17, 1, s * 0.15)));
      }
    };
    // The pack's: small, many, in rounds at the puddle's edge (they stopped to drink).
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2 + this.rnd() * 0.3;
      const r = 0.85 + (i % 2) * 0.3;
      print(px * K + Math.cos(a) * r, py * K + Math.sin(a) * r * 0.85, a + Math.PI, 0.22);
    }
    if (!wolf) return;
    // The Wolf's: great prints in a straight line past the puddle, by three stepping stones; never at the water.
    const stone = new THREE.DodecahedronGeometry(1, 0);
    const sx = -uy;
    const sy = ux;
    for (let i = -1; i <= 1; i++) kit.add(stone, '#59626b', place((px + sx * 1300 + ux * i * 650) * K, 0.03, (py + sy * 1300 + uy * i * 650) * K, i, new THREE.Vector3(0.32, 0.08, 0.28)));
    const yaw = Math.atan2(-ux, -uy);
    for (let i = -3; i <= 3; i++) {
      const x = (px + sx * (1300 + (i % 2) * 260) + ux * i * 420) * K;
      const z = (py + sy * (1300 + (i % 2) * 260) + uy * i * 420) * K;
      print(x, z, yaw, 0.46);
    }
    stone.dispose();
    pud.dispose();
    pad.dispose();
  }

  private buildHall(kit: Kit, pale: Kit, s: BattleState): void {
    const L = rockHallLayout(this.seed);
    this.hall = L;
    this.seaY = -2.1;
    const c = { x: 0, y: -400 };
    const trace = s.rules?.senda?.roca?.mouths?.trace ?? L.puzzle.trace;
    this.height = (x, y) => {
      const d = len(x - c.x, y - c.y);
      const door = seg(x, y, { x: 0, y: 5600 }, { x: 0, y: 9400 }).d;
      let h = 4.6 + noise(x / 900, y / 900) * 0.8;
      if (d < HALL_R + 200 || door < 1250) {
        const wall = Math.max(smooth(HALL_R - 300, HALL_R + 300, d) * (door < 1250 ? smooth(1000, 1400, door) : 1), 0);
        h = wall * h + (noise(x / 600, y / 600) - 0.5) * 0.05;
      }
      return h;
    };
    // The mouths' arches (and their rubble, hidden), the roof's cracks as blades of daylight on the floor.
    this.arches = L.mouths.map((m) => ({ ...this.archMesh(m, c), down: false }));
    const bladeMat = this.track(new THREE.MeshBasicMaterial({ color: DAY, transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending }));
    const links = s.rules?.senda?.roca?.mouths?.links ?? [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
    ];
    for (const [a, b] of links) {
      const A = L.arches[a] as CazaPt;
      const B = L.arches[b] as CazaPt;
      // Bowed in toward the middle a little, like a crack in a dome.
      const mid = { x: (A.x + B.x) / 2 * 0.86 + c.x * 0.14, y: (A.y + B.y) / 2 * 0.86 + c.y * 0.14 };
      const pts = [A, mid, B].map((p) => new THREE.Vector3(p.x * K, 0.05, p.y * K));
      const curve = new THREE.CatmullRomCurve3(pts);
      const ribbon = this.ribbon(curve, 0.18, 24);
      const m = this.mesh(ribbon, bladeMat.clone(), 'roca-blade');
      this.mats.push(m.material as THREE.Material);
      m.castShadow = false;
      m.receiveShadow = false;
      m.renderOrder = 3;
      this.blades.push({ mesh: m, a, b, red: 0 });
      // The shafts down from the roof's crack: glows in a line over it.
      for (let i = 1; i < 5; i++) {
        const p = curve.getPoint(i / 5);
        this.light(p.clone().setY(2.6), DAY, 2.4, 0.08, 0.2);
      }
    }
    L.mouths.forEach((m, k) => this.tracks(kit, pale, m, c, k !== trace));
    // Ivar's corner: his spear dropped, a torch out, blood dark on the ash.
    const iv = L.ivar;
    kit.add(new THREE.CylinderGeometry(0.03, 0.03, 2.2, 5), '#5b4532', place(iv.x * K + 0.6, 0.05, iv.y * K + 0.5, 0.6, 1, Math.PI / 2));
    kit.add(new THREE.CircleGeometry(1, 10).rotateX(-Math.PI / 2), '#2e1717', place(iv.x * K + 0.3, 0.03, iv.y * K + 0.2, 0, new THREE.Vector3(0.45, 1, 0.3)));
    // Bones and black stones on the floor; the wall's foot heaped.
    for (let i = 0; i < 40; i++) {
      const a = this.rnd() * Math.PI * 2;
      const d = HALL_R - 300 - this.rnd() * 900;
      this.rubbleAt(kit, (c.x + Math.cos(a) * d) * K, 0, (c.y + Math.sin(a) * d) * K, 1, 0.3, 0.35);
    }
    this.bones(pale, c, HALL_R - 1500, this.ctx.low ? 10 : 18);
    // The door's daylight, from the south.
    this.light(new THREE.Vector3(0, 1.6, 8.6), DAY, 5, 0.14, 0.1);
  }

  /** A ribbon along a curve on the floor (a blade of light), `w` wide. */
  private ribbon(curve: THREE.Curve<THREE.Vector3>, w: number, n: number): THREE.BufferGeometry {
    const pos: number[] = [];
    const idx: number[] = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const p = curve.getPoint(t);
      const d = curve.getTangent(t);
      const nx = -d.z;
      const nz = d.x;
      const ww = w * (0.35 + Math.sin(t * Math.PI) * 0.65);
      pos.push(p.x + nx * ww, p.y, p.z + nz * ww, p.x - nx * ww, p.y, p.z - nz * ww);
      if (i < n) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    return g;
  }

  /** Boar and seal bones scattered on the ash (pale, lit cold). */
  private bones(pale: Kit, c: CazaPt, r: number, n: number): void {
    const rib = new THREE.TorusGeometry(1, 0.09, 4, 10, Math.PI * 0.8);
    const skull = new THREE.DodecahedronGeometry(1, 0);
    const shaft = new THREE.CylinderGeometry(1, 1, 1, 5);
    for (let i = 0; i < n; i++) {
      const a = this.rnd() * Math.PI * 2;
      const d = Math.sqrt(this.rnd()) * r;
      const x = (c.x + Math.cos(a) * d) * K;
      const z = (c.y + Math.sin(a) * d) * K;
      const y = this.height(c.x + Math.cos(a) * d, c.y + Math.sin(a) * d);
      const k = i % 3;
      if (k === 0) for (let j = 0; j < 4; j++) pale.add(rib, '#cfd4da', place(x + j * 0.09, y + 0.05, z, a, 0.18, Math.PI / 2, 0));
      else if (k === 1) pale.add(skull, '#d6dbe0', place(x, y + 0.08, z, a, new THREE.Vector3(0.14, 0.1, 0.2)));
      else pale.add(shaft, '#c7ccd2', place(x, y + 0.03, z, a, new THREE.Vector3(0.025, 0.4, 0.025), Math.PI / 2));
    }
    rib.dispose();
    skull.dispose();
    shaft.dispose();
  }

  /* ---------- the ash bed (act 3, the bottom) ---------- */

  private buildBed(kit: Kit, pale: Kit): void {
    const L = bedLayout(this.seed);
    this.bed = L;
    this.seaY = -3;
    const HALF = 1100;
    this.height = (x, y) => {
      let open = 1e9;
      for (const r of L.rooms) open = Math.min(open, len(x - r.at.x, y - r.at.y) - r.r);
      for (const w of L.ways) open = Math.min(open, seg(x, y, w[0] as CazaPt, w[1] as CazaPt).d - HALF);
      return smooth(-250, 350, open) * (3.8 + noise(x / 800, y / 800) * 0.9) + (noise(x / 500, y / 500) - 0.5) * 0.06;
    };
    // The way on of each chamber heaped with ash and bones until its band is down.
    this.gates = L.rooms.slice(0, 2).map((room, k) => {
      const w = L.ways[k + 1] as CazaPt[];
      const a = w[0] as CazaPt;
      const b = w[1] as CazaPt;
      const t = (room.r + 900) / Math.max(1, len(b.x - a.x, b.y - a.y));
      const g = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      return this.piece((kk) => {
        const stone = new THREE.DodecahedronGeometry(1, 0);
        for (let i = 0; i < 14; i++) {
          const ox = (this.rnd() - 0.5) * 2.4;
          const oz = (this.rnd() - 0.5) * 1.4;
          kk.add(stone, i % 3 ? '#8a9096' : '#5c636b', place(g.x * K + ox, 0.25 + this.rnd() * 0.5, g.y * K + oz, this.rnd() * 6, new THREE.Vector3(0.5 + this.rnd() * 0.4, 0.4 + this.rnd() * 0.4, 0.5)));
        }
        stone.dispose();
      }, 'roca-gate');
    });
    for (let k = 0; k < L.rooms.length; k++) this.bones(pale, (L.rooms[k] as { at: CazaPt }).at, (L.rooms[k] as { r: number }).r - 400, this.ctx.low ? 7 : 12);
    // The bed: ash pressed flat in a ring, tufts of grey fur, the wooden boar in it under the shaft of daylight.
    const bx = L.bed.x * K;
    const bz = L.bed.y * K;
    const ring = new THREE.RingGeometry(0.6, 1.6, 24).rotateX(-Math.PI / 2);
    kit.add(ring, '#3b4046', place(bx, 0.03, bz, 0, 1));
    kit.add(new THREE.CircleGeometry(0.75, 20).rotateX(-Math.PI / 2), '#2a2e33', place(bx, 0.035, bz, 0, 1));
    const tuft = new THREE.IcosahedronGeometry(1, 0);
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      kit.add(tuft, '#7f8790', place(bx + Math.cos(a) * 1.3, 0.06, bz + Math.sin(a) * 1.25, a, new THREE.Vector3(0.18, 0.06, 0.1)));
    }
    tuft.dispose();
    ring.dispose();
    woodenBoar(kit, place(bx + 0.2, 0.04, bz - 0.1, 0.8, 1.15), pale);
    this.light(new THREE.Vector3(bx, 0.6, bz), DAY, 3.2, 0.22, 0.15);
    this.light(new THREE.Vector3(bx, 3.4, bz), DAY, 1.8, 0.15, 0.1);
    // The door's daylight behind, from the cave's mouth.
    this.light(new THREE.Vector3(L.start.x * K, 1.2, L.start.y * K + 0.8), DAY, 4, 0.12, 0.1);
  }

  /* ---------- the den and the summit (the speech, act 4, the close) ---------- */

  private buildDen(kit: Kit, pale: Kit, s: BattleState): void {
    const L = wolfLayout(this.seed);
    this.den = L;
    this.seaY = DEEP_SEA;
    const open = s.rules?.senda?.roca?.wolf?.open ?? [1, 1, 1, 1];
    const east = (x: number, y: number): boolean => x > DEN_AT.x + DEN_R - 600 && Math.abs(y - DEN_AT.y) < 1700;
    this.height = (x, y) => {
      const dd = len(x - DEN_AT.x, y - DEN_AT.y);
      const cr = seg(x, y, L.crackFoot, L.crackTop);
      const td = len(x - TOP_AT.x, y - TOP_AT.y);
      const nz = noise(x / 900, y / 900);
      // The den's floor (the mouths' pits in it), the crack's ramp, the summit.
      let floor = 99;
      if (dd < DEN_R) {
        floor = (noise(x / 500, y / 500) - 0.5) * 0.05;
      }
      if (cr.d < CRACK_HALF) floor = Math.min(floor, TOP_H * smooth(0.08, 0.92, cr.t));
      if (td < TOP_R) floor = Math.min(floor, TOP_H + (nz - 0.5) * 0.12);
      // What stands round the floors: the den's mouth on the sea to the east (a low sill, the drop beyond), the rock's
      // mass round the den and the crack, and off it the cliffs down to the sea.
      const mass = y > TOP_AT.y + 1600 && len(x, y - 2400) < 11800;
      // (Off the rock its cliffs go down under the water: the sea shows there, not a floor.)
      const wall = east(x, y) ? (dd < DEN_R + 900 ? 0.9 + nz * 0.2 : DEEP_SEA - 1.5) : mass ? MASS_H + nz * 0.7 : td < TOP_R + 300 ? TOP_H - 0.2 : DEEP_SEA - 1.5;
      // Inside a floor (by how far in): its own height, blended into the wall over a short way at its edge.
      const inside = Math.max(DEN_R - dd, CRACK_HALF - cr.d, TOP_R - td);
      if (floor < 99 && inside > 0) return td < TOP_R ? floor : floor + (wall - floor) * (1 - smooth(0, 320, inside));
      return wall;
    };
    const stone = new THREE.DodecahedronGeometry(1, 0);
    // The mouths: open, dark water under an arch; shut, the arch down in rubble.
    L.mouths.forEach((m, k) => {
      const a = this.archMesh(m, L.bed);
      a.rubble.visible = !open[k];
      a.stand.visible = !!open[k];
      this.arches.push({ ...a, down: !open[k] });
      if (open[k]) this.light(new THREE.Vector3(m.x * K, -1.6, m.y * K), new THREE.Color('#5d88a6'), 2.2, 0.12, 0.3);
      // Down the open mouths the sea heaves: a rim of foam round the dark water, the light off it on the stone.
      if (open[k]) {
        for (let i = 0; i < 14; i++) {
          const a = (i / 14) * Math.PI * 2 + this.rnd() * 0.3;
          pale.add(stone, '#dfe7ee', place(m.x * K + Math.cos(a) * 1.0, 0.05, m.y * K + Math.sin(a) * 0.9, a, new THREE.Vector3(0.22, 0.04, 0.12)));
        }
        this.light(new THREE.Vector3(m.x * K, 0.4, m.y * K), new THREE.Color('#7fb0d0'), 2.6, 0.22, 0.5);
      }
      // The Wolf's great prints in the ash, from its bed to every mouth it went down to drink and heal.
      if (open[k]) this.denTrail(pale, L.bed, m);
    });
    // The pillars to the roof, cracked (their cracks red), and their stumps when brought down.
    const col = new THREE.CylinderGeometry(1, 1.15, 1, 7);
    this.pillars = L.pillars.map((p) => {
      const x = p.x * K;
      const z = p.y * K;
      const up = this.piece((k) => {
        // (Three drums and a broken top, not up to the roof: from the game's high camera a full column hid the Wolf
        // fighting beside it and half the den's floor. docs/RENDIMIENTO.md, «Trampas».)
        for (let i = 0; i < 3; i++) k.add(col, i % 2 ? '#525a63' : '#465059', place(x, 0.4 + i * 0.72, z, i * 0.7, new THREE.Vector3(0.38 - i * 0.03, 0.74, 0.38 - i * 0.03)));
        k.add(stone, '#5d656e', place(x + 0.06, 2.35, z, 0.4, new THREE.Vector3(0.36, 0.3, 0.32)));
        k.add(stone, '#4b535c', place(x - 0.1, 2.55, z + 0.05, 1.3, new THREE.Vector3(0.18, 0.28, 0.16), 0.4));
        this.rubbleAt(k, x, 0, z, 4, 0.8, 0.18);
      }, 'roca-pillar');
      const down = this.piece((k) => {
        k.add(col, '#3d434b', place(x, 0.3, z, 0, new THREE.Vector3(0.5, 0.6, 0.5)));
        this.rubbleAt(k, x, 0, z, 10, 1.3, 0.32);
      }, 'roca-pillar-down');
      down.visible = false;
      // Its cracks, glowing red down the column.
      const cm = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a' }));
      for (let i = 0; i < 3; i++) {
        const c = this.mesh(new THREE.BoxGeometry(0.06, 0.55, 0.06), cm, 'roca-pillar-crack');
        const a = i * 2.1;
        c.position.set(x + Math.cos(a) * 0.38, 0.75 + i * 0.6, z + Math.sin(a) * 0.38);
        c.rotation.set(0, -a, 0.35 * (i - 1));
        c.castShadow = false;
        up.add(c);
        c.position.sub(up.position);
      }
      return { up, down, at: new THREE.Vector3(x, 0, z) };
    });
    // The slabs on the way to the mouths: grey stone, their cracks, the hole once given way.
    const crackMat = this.track(new THREE.MeshBasicMaterial({ color: '#1a1d22' }));
    const holeMat = this.track(new THREE.MeshBasicMaterial({ color: '#05090d' }));
    this.slabs = L.slabs.map((sl) => {
      const x = sl.at.x * K;
      const z = sl.at.y * K;
      const tile = this.piece((k) => {
        k.add(new THREE.BoxGeometry(1, 1, 1), '#8a9299', place(x, 0.04, z, 0.3, new THREE.Vector3(1.05, 0.08, 1.05)));
        k.add(new THREE.BoxGeometry(1, 1, 1), '#6f777f', place(x, 0.02, z, 0.3, new THREE.Vector3(1.15, 0.04, 1.15)));
      }, 'roca-slab');
      const cracks = [0, 1].map((i) => {
        const m = this.mesh(new THREE.BoxGeometry(0.06, 0.02, 0.95), crackMat, 'roca-slab-crack');
        m.position.set(x, 0.09, z);
        m.rotation.set(0, 0.3 + (i ? 1.1 : -0.4), 0);
        m.castShadow = false;
        m.visible = false;
        return m;
      });
      const hole = this.mesh(new THREE.CircleGeometry(0.55, 12), holeMat, 'roca-slab-hole');
      hole.rotation.x = -Math.PI / 2;
      hole.position.set(x, 0.05, z);
      hole.visible = false;
      return { tile, cracks, hole };
    });
    // The summit's loose stones on its rim.
    this.stones = L.stones.map((p) => {
      const x = p.x * K;
      const z = p.y * K;
      const up = this.piece((k) => {
        k.add(stone, '#5a626b', place(x, TOP_H + 0.55, z, this.rnd() * 6, new THREE.Vector3(0.75, 0.6, 0.7)));
        k.add(stone, '#474e56', place(x + 0.35, TOP_H + 0.25, z + 0.2, 1, new THREE.Vector3(0.35, 0.25, 0.3)));
      }, 'roca-stone');
      return { up, down: null, at: new THREE.Vector3(x, TOP_H, z) };
    });
    // The summit's dressing: salt-white crusts, tufts of sea grass bent by the wind, the edges' teeth.
    const tuft = new THREE.IcosahedronGeometry(1, 0);
    for (let i = 0; i < 26; i++) {
      const a = this.rnd() * Math.PI * 2;
      const d = Math.sqrt(this.rnd()) * (TOP_R - 500);
      kit.add(tuft, i % 2 ? '#6f7d62' : '#5b6a52', place((TOP_AT.x + Math.cos(a) * d) * K, TOP_H + 0.04, (TOP_AT.y + Math.sin(a) * d) * K, a, new THREE.Vector3(0.25, 0.08, 0.14)));
    }
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      const r = TOP_R + 80;
      const x = TOP_AT.x + Math.cos(a) * r;
      const y = TOP_AT.y + Math.sin(a) * r;
      if (y > TOP_AT.y + TOP_R - 900) continue;
      this.rubbleAt(kit, x * K, TOP_H - 0.15, y * K, 1, 0.2, 0.32);
    }
    tuft.dispose();
    // The den's floor: the bed in its middle, bones, the cubs' dens' scrapes.
    const bx = L.bed.x * K;
    const bz = L.bed.y * K;
    kit.add(new THREE.RingGeometry(0.6, 1.7, 24).rotateX(-Math.PI / 2), '#3b4046', place(bx, 0.03, bz, 0, 1));
    kit.add(new THREE.CircleGeometry(0.75, 20).rotateX(-Math.PI / 2), '#2a2e33', place(bx, 0.035, bz, 0, 1));
    this.bones(pale, L.bed, DEN_R - 1500, this.ctx.low ? 10 : 18);
    // Sten's small boat at the foot of the south-west wall.
    const sk = { x: L.skiff.x - 2300, y: L.skiff.y + 2300 };
    const sm = this.piece((k) => skiffBoat(k, place(sk.x * K, DEEP_SEA + 0.05, sk.y * K, -0.8, 1.1)), 'roca-skiff');
    this.skiffMesh = sm;
    this.foam(kit, [new THREE.Vector3(sk.x * K, 0, sk.y * K)], 10, 2.4);
    // The clan's boats: one model, many, rowing as a group from edge to edge below the summit.
    const bk = new Kit();
    longBoat(bk, new THREE.Matrix4());
    for (let i = 0; i < 4; i++) rower(bk, place((i % 2 ? 0.32 : -0.32), 0.12, -1.0 + i * 0.6, 0, 1), i === 0, i);
    const bm = bk.build(this.mat, 'roca-boat');
    if (bm) {
      this.geos.push(bm.geometry);
      const n = this.ctx.low ? 10 : 16;
      this.boats = new THREE.InstancedMesh(bm.geometry, this.mat, n);
      this.boats.castShadow = true;
      this.boats.frustumCulled = false;
      this.group.add(this.boats);
      for (let i = 0; i < n; i++) this.boatSpots.push({ x: ((i % 4) - 1.5) * 4.2 + (this.rnd() - 0.5), z: Math.floor(i / 4) * 5.6 + (this.rnd() - 0.5) * 1.2, yaw: (this.rnd() - 0.5) * 0.4 });
      // Waiting below the den's mouth on the sea, east (the speech).
      this.boatAt.set((DEN_AT.x + DEN_R + 6500) * K, DEEP_SEA, DEN_AT.y * K);
      this.boatTo.copy(this.boatAt);
      this.boatYaw = -Math.PI / 2;
    }
    // The White Island far to the east, white and low on the sea.
    for (let i = 0; i < 6; i++) pale.add(stone, '#e8eef4', place(70 + i * 3.2 + (this.rnd() - 0.5) * 2, DEEP_SEA - 1 + this.rnd() * 1.5, -20 + (this.rnd() - 0.5) * 6, this.rnd() * 6, new THREE.Vector3(3 + this.rnd() * 2, 1.6 + this.rnd(), 2.5)));
    // The marks of the Wolf's look (a red ring on its way out), its leap (its shadow) and its howl (round it).
    const ringMat = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }));
    this.lookRing = this.mesh(new THREE.RingGeometry(0.85, 1.1, 32), ringMat, 'roca-look');
    this.lookRing.rotation.x = -Math.PI / 2;
    this.lookRing.visible = false;
    this.lookRing.castShadow = false;
    this.lookRing.renderOrder = 4;
    const shadowMat = this.track(new THREE.MeshBasicMaterial({ color: '#0a0606', transparent: true, opacity: 0.55, depthWrite: false }));
    this.leapMark = this.mesh(new THREE.CircleGeometry(1, 28), shadowMat, 'roca-leap');
    this.leapMark.rotation.x = -Math.PI / 2;
    this.leapMark.visible = false;
    this.leapMark.castShadow = false;
    this.leapMark.renderOrder = 4;
    const edge = this.mesh(new THREE.RingGeometry(0.94, 1, 32), ringMat, 'roca-leap-edge');
    edge.rotation.x = 0;
    this.leapMark.add(edge);
    this.howlRing = this.mesh(new THREE.RingGeometry(0.9, 1, 36), ringMat.clone(), 'roca-howl');
    this.mats.push(this.howlRing.material as THREE.Material);
    this.howlRing.rotation.x = -Math.PI / 2;
    this.howlRing.visible = false;
    this.howlRing.castShadow = false;
    stone.dispose();
    col.dispose();
    // The roof's cracks over the den: daylight down on the bed.
    this.light(new THREE.Vector3(bx, 3.2, bz), DAY, 3, 0.14, 0.15);
  }

  /* ---------- the rock from the sea (the cinematic) and the White Island (the way back) ---------- */

  private clanBoats(n: number, z0: number, spread: number): void {
    const bk = new Kit();
    longBoat(bk, new THREE.Matrix4());
    for (let i = 0; i < 4; i++) rower(bk, place((i % 2 ? 0.32 : -0.32), 0.12, -1.0 + i * 0.6, 0, 1), i === 0, i);
    const bm = bk.build(this.mat, 'roca-boat');
    if (!bm) return;
    this.geos.push(bm.geometry);
    this.boats = new THREE.InstancedMesh(bm.geometry, this.mat, n);
    this.boats.castShadow = true;
    this.boats.frustumCulled = false;
    this.group.add(this.boats);
    for (let i = 0; i < n; i++) this.boatSpots.push({ x: (this.rnd() - 0.5) * spread, z: z0 + this.rnd() * spread * 0.8, yaw: Math.PI + (this.rnd() - 0.5) * 0.5 });
    this.boatAt.set(0, this.seaY, 0);
    this.boatTo.copy(this.boatAt);
  }

  private buildSea(kit: Kit, pale: Kit): void {
    this.seaY = -0.6;
    this.height = () => this.seaY + 0.28;
    // The rock: tall and black, alone in the sea, its walls streaked, the foam climbing them; a cave's mouth up top.
    const geo = new THREE.IcosahedronGeometry(1, 3);
    const p = geo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i);
      const y = p.getY(i);
      const z = p.getZ(i);
      const n = noise(x * 3 + 5, z * 3 + y * 2) * 0.35 + noise(x * 9, z * 9 + y * 5) * 0.12;
      p.setXYZ(i, x * (1 + n), Math.max(-0.2, y) * (1 + n * 0.5), z * (1 + n));
    }
    geo.computeVertexNormals();
    // Its own colours by height and face: foam at its foot, wet black streaked with the sea's grey, lichen, white guano
    // on its ledges up top (one mesh of its own, the kit would paint it one colour).
    const rock = new THREE.Group();
    for (const [x, z, yaw, sc] of [
      [0, -15, 0.3, new THREE.Vector3(11, 24, 9)],
      [-6, -12, 1.2, new THREE.Vector3(5, 9, 5)],
      [7, -13, 2, new THREE.Vector3(5, 13, 6)],
    ] as const) {
      const g = geo.clone();
      g.applyMatrix4(place(x, this.seaY - 1, z, yaw, sc));
      const gp = g.getAttribute('position') as THREE.BufferAttribute;
      const gn = g.getAttribute('normal') as THREE.BufferAttribute;
      const col = new Float32Array(gp.count * 3);
      const c = new THREE.Color();
      for (let i = 0; i < gp.count; i++) {
        const y = gp.getY(i);
        const up = gn.getY(i);
        const n = noise(gp.getX(i) * 0.7 + gp.getZ(i) * 0.3, y * 0.5);
        c.set('#14171b').lerp(new THREE.Color('#262b31'), n);
        if (n > 0.74) c.lerp(new THREE.Color('#33402f'), 0.5);
        if (up > 0.8 && y > 12 && n > 0.45) c.lerp(new THREE.Color('#9aa2aa'), 0.5);
        if (y < this.seaY + 1.2) c.lerp(new THREE.Color('#c9d6df'), 1 - Math.max(0, y - this.seaY) / 1.2);
        col[i * 3] = c.r;
        col[i * 3 + 1] = c.g;
        col[i * 3 + 2] = c.b;
      }
      g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      const m = new THREE.Mesh(this.track(g), this.mat);
      m.castShadow = true;
      m.receiveShadow = true;
      rock.add(m);
    }
    this.group.add(rock);
    geo.dispose();
    kit.add(new THREE.CircleGeometry(1, 16, 0, Math.PI), '#05080c', place(0.8, this.seaY + 18, -7.2, 0, new THREE.Vector3(1.6, 2.2, 1)));
    this.light(new THREE.Vector3(0.8, this.seaY + 18.8, -7), RED, 1.6, 0.18, 0.5);
    const ring: THREE.Vector3[] = [];
    for (let a = 0; a < Math.PI * 2; a += 0.12) ring.push(new THREE.Vector3(Math.cos(a) * 11.5, 0, -13 + Math.sin(a) * 9.5));
    this.foam(kit, ring, this.ctx.low ? 80 : 160, 2.6);
    // Sten's small boat with the hero, Terrón and Ulla in it; the clan's boats behind, far, in the fog.
    skiffBoat(kit, place(0, this.seaY, 5.6, 0, 1.25));
    this.clanBoats(this.ctx.low ? 10 : 18, 20, 30);
    void pale;
  }

  private buildIsla(kit: Kit, pale: Kit): void {
    this.seaY = -0.6;
    this.height = (x, y) => (y < -300 ? Math.min(1.3, (-y - 300) / 3800) * 1.3 + noise(x / 700, y / 700) * 0.22 : this.seaY + 0.28);
    // (A beach of dark stones at the waterline, then the sea: the boats' figures stand on it.)
    this.water = (_x, y) => y > 200;
    // The island's shore: snow on dark rock rising north, the stone of strokes, Bera's fire by it.
    const stone = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i < 30; i++) {
      const x = (this.rnd() - 0.5) * 26;
      const z = -3 - this.rnd() * 10;
      kit.add(stone, i % 3 ? '#e1e7ed' : '#4b535c', place(x, this.height(x / K, z / K) - 0.2, z, this.rnd() * 6, new THREE.Vector3(0.8 + this.rnd(), 0.4 + this.rnd() * 0.6, 0.8 + this.rnd())));
    }
    const st = { x: -1100, y: -1500 };
    kit.add(stone, '#6a727b', place(st.x * K, this.height(st.x, st.y) + 0.55, st.y * K, 0.2, new THREE.Vector3(0.45, 0.7, 0.25)));
    for (let i = 0; i < 31; i++) pale.add(new THREE.BoxGeometry(1, 1, 1), '#eef2f5', place(st.x * K - 0.3 + (i % 11) * 0.055, this.height(st.x, st.y) + 0.85 - Math.floor(i / 11) * 0.2, st.y * K + 0.26, 0, new THREE.Vector3(0.012, 0.13, 0.02)));
    stone.dispose();
    this.foam(
      kit,
      Array.from({ length: 30 }, (_, i) => new THREE.Vector3(-14 + i, 0, -0.6)),
      this.ctx.low ? 40 : 80,
      1.2,
    );
    // The boat the hero stands in, and the clan's going by.
    longBoat(kit, place(0, this.seaY, 6.4, Math.PI / 2, 1));
    this.clanBoats(this.ctx.low ? 8 : 14, 4, 26);
  }

  /* ---------- Terrón (a stone boar; the herd's own body) ---------- */

  private buildTerron(): void {
    const mat = this.track(new THREE.MeshStandardMaterial({ color: '#c9d6c4', roughness: 0.88, flatShading: true }));
    this.tBody = new THREE.Mesh(this.track(boarBody(13)), mat);
    this.tBody.castShadow = true;
    this.tBody.visible = false;
    this.group.add(this.tBody);
    const leg = this.track(boarLeg());
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(leg, mat);
      m.visible = false;
      this.tLegs.push(m);
      this.group.add(m);
    }
  }

  private drawTerron(s: BattleState | null, alpha: number): void {
    const body = this.tBody;
    if (!body) return;
    const tn = s?.senda?.npcs.find((x) => x.id === 'terron' && x.hp > 0);
    let x: number;
    let y: number;
    let facing: number;
    let moving = false;
    let lie = false;
    if (tn) {
      const t = this.tTrack;
      x = t.set ? t.px + (t.cx - t.px) * alpha : tn.x;
      y = t.set ? t.py + (t.cy - t.py) * alpha : tn.y;
      facing = tn.facing;
      moving = tn.moving;
    } else {
      const fig = s?.heroes.find((h) => h.face === 'terron');
      const p = fig ? this.ctx.pos(fig.id) : undefined;
      if (!fig || !p) {
        body.visible = false;
        for (const l of this.tLegs) l.visible = false;
        return;
      }
      x = p.x / K;
      y = p.z / K;
      facing = fig.facing ?? 0;
      lie = this.place === 'rocaMouth';
    }
    const sc = SCALE.hero * 1.2;
    const g = this.ctx.at(x, y, lie ? -0.08 : 0);
    const stride = moving ? this.tTrack.dist * 9.5 : 0;
    const bob = moving ? Math.abs(Math.sin(stride)) * 0.025 : Math.max(0, Math.sin(this.now * 0.9)) * 0.01;
    const yaw = facingToRotY(facing);
    this.e3.set(0, yaw, Math.sin(stride) * 0.03, 'YXZ');
    body.position.set(g.x, g.y + bob, g.z);
    body.quaternion.setFromEuler(this.e3);
    body.scale.setScalar(sc);
    body.visible = true;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    this.tLegs.forEach((l, i) => {
      const [lx, lz] = BOAR_LEGS[i] as [number, number];
      const ph = i === 0 || i === 3 ? 1 : -1;
      this.e3.set(lie ? 1.2 * ph : Math.sin(stride) * 0.5 * ph, yaw, 0, 'YXZ');
      l.quaternion.setFromEuler(this.e3);
      l.position.set(g.x + (lx * cy + lz * sy) * sc, g.y + BOAR_HIP_Y * sc + bob, g.z + (-lx * sy + lz * cy) * sc);
      l.scale.setScalar(sc);
      l.visible = true;
    });
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    this.terrain = terrain;
    terrain.mesh.visible = false;
    terrain.decor.traverse((o) => {
      if (o.name === 'roads' || o.name === 'outskirts-ground') o.visible = false;
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (terrain.fog) terrain.fog.group.visible = false;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint();
  }

  /** The board's light by the hour (a scene's sky, when it gives one; the caves keep their own). */
  paint(sky?: 'day' | 'dusk' | 'night' | 'dawn'): void {
    if (sky && this.hour !== 'inside') this.hour = sky;
    const look = HOURS[this.hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(this.hour === 'dusk' ? 0.3 : -0.3, this.hour === 'dusk' ? 0.06 : this.hour === 'dawn' ? 0.12 : 0.4, this.hour === 'dawn' ? 0.9 : -0.9);
    this.paintSea();
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.last = s;
    const t = s.senda?.npcs.find((n) => n.id === 'terron');
    if (t) {
      const k = this.tTrack;
      if (!k.set) Object.assign(k, { px: t.x, py: t.y, cx: t.x, cy: t.y, dist: 0, set: true });
      else {
        k.dist += Math.hypot(t.x - k.cx, t.y - k.cy) * K;
        k.px = k.cx;
        k.py = k.cy;
        k.cx = t.x;
        k.cy = t.y;
      }
    }
    this.syncState(s);
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      switch (e.kind) {
        case 'rStrike':
          this.ctx.vfx.burst(p.clone().setY(p.y + 2), { n: 8, color: '#6d757e', speed: 0.05, up: 0.03, size: 0.08, life: 26, gravity: 0.006, spread: 0.5 });
          this.ctx.shake(0.06);
          break;
        case 'rCrack':
          this.ctx.flare(p.clone().setY(p.y + 2.2), '#ff4f3a', 2.2, 18);
          break;
        case 'rFall': {
          const a = this.arches[e.n ?? -1];
          if (a) {
            a.down = true;
            a.stand.visible = false;
            a.rubble.visible = true;
          }
          for (const b of this.blades) if (b.a === e.n || b.b === e.n) b.red = 1;
          this.ctx.vfx.burst(p.clone().setY(p.y + 1), { n: 26, color: '#4b535c', speed: 0.09, up: 0.08, size: 0.14, life: 40, gravity: 0.006, spread: 1.4 });
          // The sea out of the cracks in the rock, in a burst of spray.
          this.ctx.vfx.burst(p.clone().setY(p.y - 0.5), { n: 30, color: '#e8f1f6', speed: 0.12, up: 0.18, size: 0.09, life: 46, gravity: 0.008, spread: 0.9 });
          this.ctx.shake(0.32);
          break;
        }
        case 'rSurge':
          // The pack out of its lair through the cracks: a burst of ash and a red ring where it comes up.
          this.ctx.vfx.burst(p.clone().setY(p.y + 0.6), { n: 34, color: '#8a929b', speed: 0.1, up: 0.1, size: 0.16, life: 44, gravity: 0.004, spread: 1.6 });
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.1), 0.4, 4.5, '#ff4f3a', 26, 0.8);
          this.ctx.shake(0.22);
          break;
        case 'rHorn':
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.1), 0.4, 7, '#dfe9f6', 26, 0.7);
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, 5, '#ff8a6e', 20, 0.5);
          break;
        case 'rRear':
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.6, '#ff4f3a', 16, 0.8);
          break;
        case 'rHowl':
        case 'rHowled':
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.4), 0.4, 5.5, '#ff4f3a', 30, 0.5);
          break;
        case 'rHowlCut':
        case 'rCut':
          this.ctx.flare(p.clone().setY(p.y + 0.8), '#ffffff', 2.6, 16);
          this.ctx.vfx.burst(p.clone().setY(p.y + 0.6), { n: 12, color: '#ff8a6e', speed: 0.05, up: 0.04, size: 0.06, life: 22, gravity: 0.004, spread: 0.6 });
          break;
        case 'rOpen': {
          const g = this.gates[e.n ?? -1];
          if (g) {
            g.visible = false;
            const c = (g.geometry.boundingSphere?.center ?? new THREE.Vector3()).clone();
            this.ctx.vfx.burst(c.setY(0.6), { n: 30, color: '#b9bfc6', speed: 0.06, up: 0.05, size: 0.12, life: 48, gravity: 0.002, spread: 1.5 });
          }
          break;
        }
        case 'rBed':
          this.ctx.flare(p.clone().setY(p.y + 0.6), '#dfe9f6', 3.5, 30);
          break;
        case 'rDive':
        case 'rRise':
          this.ctx.vfx.burst(p.clone().setY(this.seaY + 0.5 > p.y ? p.y : p.y - 1), { n: 24, color: '#e8f1f6', speed: 0.1, up: 0.15, size: 0.08, life: 40, gravity: 0.008, spread: 0.8 });
          break;
        case 'rLeap':
          this.ctx.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 2.2, '#6a6f76', 18, 0.7);
          this.ctx.vfx.burst(p, { n: 14, color: '#80878e', speed: 0.06, up: 0.05, size: 0.1, life: 30, gravity: 0.004, spread: 1 });
          this.ctx.shake(e.n ? 0.25 : 0.12);
          break;
        case 'rDrop':
        case 'rStuck':
          this.ctx.vfx.burst(p.clone().setY(p.y + 2.5), { n: 22, color: '#4b535c', speed: 0.06, up: -0.05, size: 0.14, life: 34, gravity: 0.012, spread: 1.1 });
          this.ctx.shake(0.22);
          break;
        case 'rSlabGive':
          this.ctx.vfx.burst(p, { n: 14, color: '#7c848c', speed: 0.06, up: 0.04, size: 0.1, life: 30, gravity: 0.01, spread: 0.6 });
          break;
        case 'rLast':
          this.ctx.flare(p.clone().setY(p.y + 1), '#ff4f3a', 4, 30);
          break;
        case 'rGnaw':
          if (this.skiffMesh) this.ctx.vfx.burst((this.skiffMesh.geometry.boundingSphere?.center ?? p).clone().setY(DEEP_SEA + 0.6), { n: 6, color: '#6a5846', speed: 0.05, up: 0.06, size: 0.06, life: 22, gravity: 0.008, spread: 0.5 });
          break;
        case 'rAsh':
          // The Wolf comes apart into ash and the wind takes it over the sea.
          for (let i = 0; i < 3; i++) this.ctx.vfx.burst(p.clone().setY(p.y + 0.6 + i * 0.4), { n: 30, color: i ? '#9aa1a8' : '#c9ced4', speed: 0.04, up: 0.03, size: 0.12, life: 90, gravity: -0.0015, spread: 1.6 });
          this.ctx.flare(p.clone().setY(p.y + 1), '#dfe9f6', 5, 40);
          break;
        default:
          break;
      }
    }
  }

  /** What the sim says now, put on the pieces (also after a jump back in a replay). */
  private syncState(s: BattleState): void {
    const m = s.senda?.roca?.mouths;
    if (m) {
      const key = m.shut.join('') + m.cracks.join('');
      if (key !== this.cracksSeen) {
        this.cracksSeen = key;
        this.arches.forEach((a, k) => {
          const down = !!m.shut[k];
          a.stand.visible = !down;
          a.rubble.visible = down;
          a.down = down;
          a.cracks.forEach((c, i) => (c.visible = !down && i < (m.cracks[k] ?? 0)));
        });
      }
    }
    const b = s.senda?.roca?.bed;
    if (b && b.cleared !== this.gatesOpen) {
      this.gatesOpen = b.cleared;
      this.gates.forEach((g, k) => (g.visible = k >= b.cleared));
    }
    const w = s.senda?.roca?.wolf;
    if (w) {
      this.pillars.forEach((p, k) => {
        p.up.visible = !!w.pillars[k];
        if (p.down) p.down.visible = !w.pillars[k];
      });
      this.stones.forEach((p, k) => (p.up.visible = !!w.stones[k]));
      const key = w.slabs.join('');
      if (key !== this.slabKey) {
        this.slabKey = key;
        this.slabs.forEach((sl, k) => {
          const n = w.slabs[k] ?? 0;
          sl.tile.visible = n < 3;
          sl.hole.visible = n >= 3;
          sl.cracks.forEach((c, i) => (c.visible = n < 3 && i < n));
        });
      }
      // The boats row under the edge they close (out from it, on the sea far below).
      const r = s.rules?.senda?.roca?.wolf;
      if (r && w.boats >= 0) {
        const e = r.edges[w.boats] as CazaPt;
        const dx = e.x - r.summit.x;
        const dy = e.y - r.summit.y;
        const d = Math.max(1, Math.hypot(dx, dy));
        this.boatTo.set((e.x + (dx / d) * 6500) * K, DEEP_SEA, (e.y + (dy / d) * 6500) * K);
        this.boatYaw = Math.atan2(-dx, -dy);
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
  }

  /** The Wolf's marks: its look at the way out, its leap's shadow, its howl. */
  private frameWolf(now: number): void {
    const s = this.last;
    const w = s?.senda?.roca?.wolf;
    const r = s?.rules?.senda?.roca?.wolf;
    if (!s || !w || !r) {
      if (this.lookRing) this.lookRing.visible = false;
      if (this.leapMark) this.leapMark.visible = false;
      if (this.howlRing) this.howlRing.visible = false;
      return;
    }
    const e = s.enemies.find((x) => x.id === w.id && x.hp > 0);
    // The Wolf itself, so it never sinks into its ash: a cold pale haze about it and its red eyes burning (glow
    // sprites, no light).
    if (e && w.mode !== 3) {
      const at = this.at(e, 0);
      this.glow.add(at.x, at.y + 1.0, at.z, ASH, 4.2, 0.26);
      const f = ((e.facing ?? 0) / 1024) * Math.PI * 2;
      this.glow.add(at.x + Math.cos(f) * 1.25, at.y + 1.6, at.z + Math.sin(f) * 1.25, RED, 0.9, 0.8 + Math.sin(now * 6) * 0.15);
    }
    if (this.lookRing) {
      const on = (w.mode === 1 || w.mode === 2 || w.mode === 11) && w.exit >= 0;
      this.lookRing.visible = on;
      if (on) {
        const p = (w.exitKind === 0 ? r.mouths[w.exit] : r.edges[w.exit]) as CazaPt;
        const at = this.at(p, 0.12);
        if (w.exitKind === 0) at.y = 0.12;
        this.lookRing.position.copy(at);
        this.lookRing.scale.setScalar(1.6 + Math.sin(now * 8) * 0.12);
        this.glow.add(at.x, at.y + 0.3, at.z, RED, 3, 0.3 + Math.sin(now * 8) * 0.1);
      }
    }
    if (this.leapMark) {
      const on = w.mode === 5 || w.mode === 6;
      this.leapMark.visible = on;
      if (on) {
        const k = w.mode === 5 ? 1 - Math.max(0, w.until - s.tick) / Math.max(1, r.leap.warn) : 1;
        const at = this.at(w.leapAt, 0.06);
        this.leapMark.position.copy(at);
        this.leapMark.scale.setScalar(((r.leap.r * K) / 1) * (0.55 + k * 0.45));
      }
    }
    if (this.howlRing) {
      const on = !!e && (w.mode === 7 || w.mode === 8);
      this.howlRing.visible = on;
      if (on && e) {
        const at = this.at(e, 0.08);
        this.howlRing.position.copy(at);
        const k = w.mode === 8 ? 1.4 + ((now * 2.2) % 1) * 2 : 1.2 + Math.sin(now * 10) * 0.15;
        this.howlRing.scale.setScalar(k);
        (this.howlRing.material as THREE.MeshBasicMaterial).opacity = w.mode === 8 ? 0.9 - ((now * 2.2) % 1) * 0.7 : 0.8;
      }
    }
    // Ulla's horn, and Ivar up, are the people's own; the pillars' cracks breathe a little.
    for (const p of this.pillars) if (p.up.visible) this.glow.add(p.at.x, 2.2, p.at.z, RED, 1.2, 0.12 + Math.sin(now * 2 + p.at.x) * 0.04);
  }

  private frameBoats(now: number, dt: number): void {
    const b = this.boats;
    if (!b) return;
    // Rowing toward where they go (a few seconds' way), rocking on the swell.
    this.boatAt.lerp(this.boatTo, Math.min(1, dt * 0.35));
    const yaw = this.boatYaw;
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    this.boatSpots.forEach((p, i) => {
      const x = this.boatAt.x + p.x * c + p.z * sn;
      const z = this.boatAt.z - p.x * sn + p.z * c;
      this.dummy.position.set(x, this.boatAt.y + Math.sin(now * 1.3 + i) * 0.06, z);
      this.dummy.rotation.set(Math.sin(now * 0.9 + i * 0.7) * 0.03, yaw + p.yaw, Math.sin(now * 1.1 + i) * 0.04);
      this.dummy.scale.setScalar(0.9);
      this.dummy.updateMatrix();
      b.setMatrixAt(i, this.dummy.matrix);
    });
    b.instanceMatrix.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera, alpha = 1): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    this.air?.frame(now, this.look);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    this.drawTerron(this.last, alpha);
    this.glow.begin();
    this.frameBoats(now, dt);
    if (this.den) this.frameWolf(now);
    for (const b of this.blades) {
      const m = b.mesh.material as THREE.MeshBasicMaterial;
      b.red = Math.max(0, b.red - dt * 0.5);
      m.color.copy(DAY).lerp(RED, b.red);
      m.opacity = 0.42 + Math.sin(now * 0.7 + b.a) * 0.06 + b.red * 0.4;
    }
    for (const a of this.arches) if (!a.down) for (const c of a.cracks) if (c.visible) this.glow.add(c.position.x, c.position.y, c.position.z, RED, 0.8, 0.35 + Math.sin(now * 5 + c.position.x) * 0.1);
    for (const l of this.lights) {
      const fl = l.flick ? 0.84 + 0.16 * Math.sin(now * (6 + l.seed) + l.seed) * l.flick + 0.06 * Math.sin(now * 17 + l.seed * 3) * l.flick : 1;
      if (l.a > 0) this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * fl);
    }
    this.glow.end();
    void RED_SOFT;
    void ASH;
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.air?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
    void this.terrain;
  }
}
